/**
 * Startseite mit A/B-Test.
 *
 * Laeuft kein Test, rendert exakt das, was vorher an dieser Stelle stand — die
 * Test-Logik liegt also nicht im normalen Pfad, sondern daneben. Faellt der
 * Endpunkt aus, ist der Standardfall unveraendert erreichbar.
 */
import React from 'react';
import OptimizedLandingPage from '../components/landing/OptimizedLandingPage';
import { shouldUseRedesignForSurface, type UiReleaseMode } from '../config/uiRelease';
import { useExperimentVariant, type LandingComponentId } from '../config/experiments';

const LandingRedesignPage = React.lazy(() => import('../components/landing/LandingRedesignPreview'));
const LandingRedesignV3Page = React.lazy(() => import('../components/landing/LandingRedesignV3'));
const LandingV4Page = React.lazy(() => import('../components/landing/LandingV4'));

interface HomepageSurfaceProps {
  uiReleaseMode: UiReleaseMode;
  onLogin: () => void;
  onNavigate: (page: string) => void;
}

const HomepageSurface: React.FC<HomepageSurfaceProps> = ({ uiReleaseMode, onLogin, onNavigate }) => {
  const assignment = useExperimentVariant('homepage');

  const renderDefault = () => (
    shouldUseRedesignForSurface(uiReleaseMode, 'homepage')
      ? <LandingRedesignPage />
      : <OptimizedLandingPage onLogin={onLogin} onNavigate={onNavigate} />
  );

  if (!assignment) return renderDefault();

  const byComponent: Record<LandingComponentId, () => React.ReactElement> = {
    'landing-redesign': () => <LandingRedesignPage />,
    'landing-optimized': () => <OptimizedLandingPage onLogin={onLogin} onNavigate={onNavigate} />,
    'landing-v3': () => <LandingRedesignV3Page />,
    'landing-v4': () => <LandingV4Page />,
  };

  const render = byComponent[assignment.component];
  // Eine Variante, die auf eine entfernte Komponente zeigt, faellt auf die
  // Standardseite zurueck statt die Startseite zu leeren.
  return render ? render() : renderDefault();
};

export default HomepageSurface;
